/**
 * Server functions for CIH frameworks.
 *
 * Frameworks are methodology/standard pages managed by admins.
 * They have a `type` (FrameworkType enum) and optional `ccoId`.
 * Lifecycle transitions delegate to content-lifecycle helpers.
 *
 * Aliases: frameworks, cih frameworks, framework crud, framework lifecycle
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { permissionMiddleware } from "~/lib/permission-middleware";
import { FrameworkType } from "~/lib/schemas/db-enums-cih";
import {
  createFrameworkInputSchema,
  updateFrameworkInputSchema,
  type FrameworkListRow,
  type FrameworkRow,
  type FrameworkWithRelations,
  type PaginatedFrameworkCoaches,
} from "~/lib/server/cih/frameworks";

// ============================================================================
// Types (re-exported from shared server module)
// ============================================================================

export type {
  FrameworkListRow,
  FrameworkRow,
  FrameworkWithRelations,
  FrameworkCoachRow,
  PaginatedFrameworkCoaches,
} from "~/lib/server/cih/frameworks";

// ============================================================================
// Query Keys
// ============================================================================

export const frameworkQueryKeys = {
  all: ["frameworks"] as const,
  list: (status?: string) => ["frameworks", "list", status ?? "all"] as const,
  detail: (id: string) => ["frameworks", id] as const,
  published: (slug: string) => ["frameworks", "published", slug] as const,
  publishedAll: ["frameworks", "published", "all"] as const,
};

// ============================================================================
// Server Functions — Reads (delegate to shared server layer)
// ============================================================================

/**
 * List all frameworks (admin). Excludes draft-of rows by default.
 * Optionally filter by status and/or type.
 */
export const getFrameworksFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(
    z
      .object({
        status: z.string().optional(),
        type: z.nativeEnum(FrameworkType).optional(),
      })
      .optional(),
  )
  .handler(async ({ data }): Promise<FrameworkListRow[]> => {
    const { listFrameworks } = await import("~/lib/server/cih/frameworks");
    return listFrameworks(data);
  });

/**
 * Get a single framework by ID (admin).
 */
export const getFrameworkFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }): Promise<FrameworkRow | null> => {
    const { getFramework } = await import("~/lib/server/cih/frameworks");
    return getFramework(data);
  });

/**
 * Get a published framework by slug (public — no auth required).
 */
export const getPublishedFrameworkFn = createServerFn({ method: "POST" })
  .validator(z.object({ slug: z.string() }))
  .handler(async ({ data }): Promise<FrameworkRow | null> => {
    const { getPublishedFramework } =
      await import("~/lib/server/cih/frameworks");
    return getPublishedFramework(data);
  });

/**
 * Get a published framework by slug with related CCO, challenges, and CTPs.
 * Public — no auth required.
 */
export const getPublishedFrameworkWithRelationsFn = createServerFn({
  method: "POST",
})
  .validator(z.object({ slug: z.string() }))
  .handler(async ({ data }): Promise<FrameworkWithRelations | null> => {
    const { getPublishedFrameworkWithRelations } =
      await import("~/lib/server/cih/frameworks");
    return getPublishedFrameworkWithRelations(data);
  });

/**
 * List all published frameworks (public — no auth required).
 * Returns the browse-page fields with aggregate counts of coaches,
 * programs, and challenges.
 */
export const getPublishedFrameworksFn = createServerFn({
  method: "POST",
}).handler(async () => {
  const { listPublishedFrameworks } =
    await import("~/lib/server/cih/frameworks");
  return listPublishedFrameworks();
});

/**
 * Get challenge IDs linked to a framework (admin).
 */
export const getFrameworkChallengeIdsFn = createServerFn({
  method: "POST",
})
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ frameworkId: z.string().uuid() }))
  .handler(async ({ data }): Promise<string[]> => {
    const { getFrameworkChallengeIds } =
      await import("~/lib/server/cih/frameworks");
    return getFrameworkChallengeIds(data);
  });

/**
 * Get coaches who use a specific framework (public).
 * Returns coaches with the profile data the framework coach lists show.
 *
 * Supports pagination via `page` and `limit`. `total` reflects the full
 * filtered coach count across all pages.
 */
export const getCoachesByFrameworkFn = createServerFn({ method: "POST" })
  .validator(
    z.object({
      frameworkId: z.string().uuid(),
      page: z.number().int().min(1).optional(),
      limit: z.number().int().min(1).max(100).optional(),
    }),
  )
  .handler(async ({ data }): Promise<PaginatedFrameworkCoaches> => {
    const { getCoachesByFramework } =
      await import("~/lib/server/cih/frameworks");
    return getCoachesByFramework(data);
  });

/**
 * Get the full list of CTPs that teach a published framework by slug.
 * Returns the framework basics alongside the CTPs so the page has a name
 * to render in the hero. Public — no auth required.
 */
export const getFrameworkCtpsFn = createServerFn({ method: "GET" })
  .validator(z.object({ slug: z.string() }))
  .handler(
    async ({
      data,
    }): Promise<{
      framework: FrameworkRow | null;
      ctps: FrameworkWithRelations["ctps"];
    }> => {
      const { getFrameworkCtps } = await import("~/lib/server/cih/frameworks");
      return getFrameworkCtps(data);
    },
  );

/**
 * Get all published frameworks grouped by type (for selection UI).
 * Public — no auth required.
 */
export const getGroupedFrameworksFn = createServerFn({
  method: "POST",
}).handler(async () => {
  const { getGroupedFrameworks } = await import("~/lib/server/cih/frameworks");
  return getGroupedFrameworks();
});

// ============================================================================
// Server Functions — Writes (delegate to shared server layer)
// ============================================================================

/**
 * Create a new framework (admin). authorId is set from the session.
 */
export const createFrameworkFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(createFrameworkInputSchema)
  .handler(async ({ data, context }): Promise<FrameworkRow> => {
    const { createFramework } = await import("~/lib/server/cih/frameworks");
    return createFramework(data, { userId: context.user.id });
  });

/**
 * Update framework fields (admin, partial update).
 */
export const updateFrameworkFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(updateFrameworkInputSchema)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { updateFramework } = await import("~/lib/server/cih/frameworks");
    return updateFramework(data);
  });

/**
 * Sync challenge links for a framework (admin).
 * Computes diff: deletes removed, inserts new.
 */
export const syncFrameworkChallengesFn = createServerFn({
  method: "POST",
})
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      frameworkId: z.string().uuid(),
      challengeIds: z.array(z.string().uuid()),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { syncFrameworkChallenges } =
      await import("~/lib/server/cih/frameworks");
    return syncFrameworkChallenges(data);
  });

// ============================================================================
// Server Functions — Lifecycle (delegate to shared server layer)
// ============================================================================

/**
 * Admin self-publish a draft framework (skips review).
 */
export const publishFrameworkFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { publishFramework } = await import("~/lib/server/cih/frameworks");
    return publishFramework(data, { userId: context.user.id });
  });

/**
 * Archive a published framework.
 */
export const archiveFrameworkFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { archiveFramework } = await import("~/lib/server/cih/frameworks");
    return archiveFramework(data, { userId: context.user.id });
  });

/**
 * Create an editable draft of a published framework.
 */
export const createFrameworkDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ publishedId: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<FrameworkRow> => {
    const { createDraftOfPublishedFramework } =
      await import("~/lib/server/cih/frameworks");
    return createDraftOfPublishedFramework(data, { userId: context.user.id });
  });

/**
 * Promote a draft back onto its published parent.
 */
export const promoteFrameworkDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ draftId: z.string().uuid() }))
  .handler(async ({ data }): Promise<FrameworkRow> => {
    const { promoteFrameworkDraft } =
      await import("~/lib/server/cih/frameworks");
    return promoteFrameworkDraft(data);
  });

/**
 * Restore an archived framework to draft.
 */
export const restoreFrameworkFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { restoreFramework } = await import("~/lib/server/cih/frameworks");
    return restoreFramework(data, { userId: context.user.id });
  });

/**
 * Approve an in_review framework (publish it).
 */
export const approveFrameworkFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { approveFramework } = await import("~/lib/server/cih/frameworks");
    return approveFramework(data, { userId: context.user.id });
  });

/**
 * Return an in_review framework to draft with feedback.
 */
export const returnFrameworkToDraftFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid(), note: z.string().min(1) }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { returnFrameworkToDraft } =
      await import("~/lib/server/cih/frameworks");
    return returnFrameworkToDraft(data, { userId: context.user.id });
  });

/**
 * Submit a framework draft for review.
 */
export const submitFrameworkForReviewFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data, context }): Promise<{ success: boolean }> => {
    const { submitFrameworkForReview } =
      await import("~/lib/server/cih/frameworks");
    return submitFrameworkForReview(data, { userId: context.user.id });
  });

/**
 * List frameworks belonging to a CCO (admin). Used by the CCO editor's reverse-lookup panel.
 */
export const getFrameworksByCcoFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ ccoId: z.string().uuid() }))
  .handler(async ({ data }): Promise<FrameworkListRow[]> => {
    const { listFrameworks } = await import("~/lib/server/cih/frameworks");
    return listFrameworks({ ccoId: data.ccoId });
  });

/**
 * Get CTPs that teach this framework by framework ID (admin).
 * Unlike getFrameworkCtpsFn (slug-based), this works for drafts too.
 */
export const getFrameworkCtpsByIdFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ frameworkId: z.string().uuid() }))
  .handler(async ({ data }): Promise<FrameworkWithRelations["ctps"]> => {
    const { getFrameworkCtpsById } =
      await import("~/lib/server/cih/frameworks");
    return getFrameworkCtpsById(data);
  });

/**
 * Count coaches who claim a specific framework. Returns a plain count for summary display.
 */
export const getFrameworkCoachCountFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ frameworkId: z.string().uuid() }))
  .handler(async ({ data }): Promise<number> => {
    const result = await getCoachesByFrameworkFn({
      data: { frameworkId: data.frameworkId, page: 1, limit: 1 },
    });
    return result.total;
  });

// ============================================================================
// Query Options
// ============================================================================

export const frameworksQueryOptions = (status?: string) =>
  queryOptions({
    queryKey: frameworkQueryKeys.list(status),
    queryFn: () => getFrameworksFn({ data: { status } }),
  });

export const frameworkQueryOptions = (id: string) =>
  queryOptions({
    queryKey: frameworkQueryKeys.detail(id),
    queryFn: () => getFrameworkFn({ data: { id } }),
  });

export const publishedFrameworkQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: frameworkQueryKeys.published(slug),
    queryFn: () => getPublishedFrameworkFn({ data: { slug } }),
  });

export const publishedFrameworkWithRelationsQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: [...frameworkQueryKeys.published(slug), "relations"] as const,
    queryFn: () => getPublishedFrameworkWithRelationsFn({ data: { slug } }),
  });

export const publishedFrameworksQueryOptions = () =>
  queryOptions({
    queryKey: frameworkQueryKeys.publishedAll,
    queryFn: () => getPublishedFrameworksFn(),
  });
